/**
 * @file        src/pages/seo/SeoPages.jsx
 * @description SEO Management → Page SEO. One row per INDEXABLE storefront page
 *              in the shared route registry — all of them, including the ones
 *              nobody has configured, so an unconfigured page is visible rather
 *              than absent. Search-results and checkout pages are not listed:
 *              they are always noindex and have nothing to edit.
 *
 *              The Title column and the Indexed chip are the RESOLVED values
 *              (what the storefront serves, fallbacks applied), while the Title
 *              and Description chips say whether the page has its OWN — the two
 *              questions an SEO pass asks of every row.
 * @author      Kinetika Labs
 * @created     2026-10-09
 * @changelog
 *   - 2026-10-09 Claude — Initial (SEO Management).
 */
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Chip from '../../components/Chip';
import TableSkeleton from '../../components/TableSkeleton';
import { listSeoPages } from '../../api/seo';
import { qk } from '../../api/queryKeys';
import { formatDateTime } from '../../lib/format';
import { BrandBar, NoindexBanner, PickBrand, useSeoBrand, withBrand } from './seoShared';

const TITLE_CELL = 46;

function Flag({ on, label }) {
  return <Chip tone={on ? 'ok' : 'pend'}>{on ? '✓' : '—'} {label}</Chip>;
}

export default function SeoPages() {
  const navigate = useNavigate();
  const [brand, setBrand] = useSeoBrand();

  const { data, isLoading, isError } = useQuery({
    queryKey: qk.seo.pages(brand),
    queryFn: () => listSeoPages(brand),
    enabled: Boolean(brand),
    placeholderData: keepPreviousData,
  });
  const rows = data?.data || [];
  const meta = data?.meta || {};

  return (
    <>
      <PageHead title="Page SEO" subtitle="SEO Management · titles, descriptions and social cards for each storefront page" />
      <BrandBar brand={brand} onChange={setBrand} />
      {!brand ? <PickBrand /> : (
        <>
          <NoindexBanner reason={meta.siteNoindexReason} brand={brand} />
          <article className="panel glass">
            <div className="table-wrap">
              {isLoading ? <TableSkeleton /> : isError ? (
                <div className="empty"><p className="field-error">Could not load pages.</p></div>
              ) : (
                <table className="table">
                  <thead>
                    <tr><th>Page</th><th>Path</th><th>Title (as served)</th><th>Status</th><th>Last updated</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const t = r.resolved?.title || '';
                      return (
                        <tr
                          key={r.pageKey}
                          className="seo-row-click"
                          onClick={() => navigate(withBrand(`/seo/pages/${encodeURIComponent(r.pageKey)}`, brand))}
                          style={{ verticalAlign: 'middle' }}
                        >
                          <td>
                            <b>{r.label}</b>
                            {r.template ? <div className="muted" style={{ fontSize: '.72rem' }}>Template for every item without its own SEO</div> : null}
                            {r.optional ? (
                              <div className="muted" style={{ fontSize: '.72rem', whiteSpace: 'normal', maxWidth: 260 }}>
                                Exists only on some storefronts — include in sitemap only for the brand that has this page.
                              </div>
                            ) : null}
                          </td>
                          <td className="mono">{r.pattern}</td>
                          <td>{t.length > TITLE_CELL ? `${t.slice(0, TITLE_CELL).trimEnd()}…` : t}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 300 }}>
                              <Flag on={r.chips.hasTitle} label="Title" />
                              <Flag on={r.chips.hasDescription} label="Description" />
                              <Flag on={r.chips.hasOgImage} label="OG image" />
                              <Chip tone={r.chips.indexed ? 'ok' : 'rej'}>{r.chips.indexed ? 'Indexed' : 'Noindex'}</Chip>
                            </div>
                          </td>
                          <td>{r.updated_at ? formatDateTime(r.updated_at) : <span className="muted">Not configured</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </article>
        </>
      )}
    </>
  );
}
